import { useEffect, useState } from "react";
import { Stepper, Card, Center, Loader, Button, Group, Modal, Textarea, Text } from "@mantine/core";
import { get, post } from "../tools/Request";
import { useUser } from "../tools/Context";
import { IconEye, IconX } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons-react";

export default function DisplayDecompteWorkflow({ decompteId }) {
  const user = useUser();
  const [decompte, setDecompte] = useState(null);
  const [signers, setSigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reserveComment, setReserveComment] = useState("");

  useEffect(() => {
    const fetchDecompte = async () => {
      try {
        const result = await get(`decompte/${decompteId}`, null, user.token);
        setDecompte(result.decompte);
        setSigners(result.signers);
      } catch (err) {
        console.error("Error fetching decompte:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDecompte();
  }, [decompteId, user.token]);

  const handleSign = async (status, comment = "") => {
    setSigning(true);
    try {
      await post("sign/signDecompte", { decompteId, status, comment }, user.token);
      const updated = await get(`decompte/${decompteId}`, null, user.token);
      setSigners(updated.signers);
      setShowReserveModal(false);
      setReserveComment("");

      showNotification({
        title: "Signature enregistrée",
        message:
          status === "approved"
            ? "Signé sans réserve"
            : status === "reserved"
            ? "Signé avec réserve"
            : "Décompte refusé",
        color: status === "rejected" ? "red" : status === "reserved" ? "yellow" : "green",
        icon: <IconCheck size={18} />,
      });
    } catch (error) {
      console.error("Error signing decompte:", error);
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <Center mt={60}>
        <Loader />
      </Center>
    );
  }

  const nextSignerIndex = signers.findIndex((s) => s.role === "signer" && !s.signed);
  const activeStep = nextSignerIndex === -1 ? signers.length : nextSignerIndex;

  const isUserTurn =
    signers[activeStep]?.role === "signer" &&
    signers[activeStep]?.signer_id === user.id &&
    !signers[activeStep]?.signed;

  const isFirstSigner =
    signers.length > 0 &&
    signers[0].signer_id === user.id &&
    !signers[0].signed &&
    signers.every((s, i) => i === 0 || !s.signed);

  const signerSteps = signers.filter((s) => s.role === "signer");
  const lastSigner = signerSteps[signerSteps.length - 1];
  const isLastSigner = lastSigner?.signer_id === user.id;
  const hasLastSignerSigned = isLastSigner && lastSigner?.signed;
  const someoneRejected = signers.some((s) => s.status === "rejected");

  const handleDelete = async () => {
    setSigning(true);
    try {
      await post(`decompte/delete/${decompteId}`, {}, user.token);
      showNotification({
        title: "Décompte supprimé",
        message: "Le décompte a été supprimé avec succès.",
        color: "red",
        icon: <IconX size={18} />,
      });
      window.location.href = "/mydecompte";
    } catch (error) {
      console.error("Erreur suppression décompte:", error);
      showNotification({
        title: "Erreur",
        message: "Impossible de supprimer ce décompte.",
        color: "red",
        icon: <IconX size={18} />,
      });
    } finally {
      setSigning(false);
    }
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <Center>
      <Card shadow="sm" padding="lg" radius="md" withBorder w="100%" mt={10}>
        {hasLastSignerSigned ? (
          <Text align="center" size="md">
            Décompte {lastSigner.status === "rejected" ? "rejeté" : "accepté"} le {formatDate(lastSigner.signed_at)}
          </Text>
        ) : (
          <>
            {user.role === "titulaire de marché" ? (
              <Stepper active={1}>
                <Stepper.Step key="step-company1" label="1. RATP" description="Étape initiale" completed />
                {(() => {
                  const currentSigner = signers.find((s) => s.signer_id === user.id);
                  if (!currentSigner) return null;

                  const description =
                    currentSigner.status === "rejected" ? "Rejeté" : currentSigner.signed ? "Signé" : "En attente";

                  return (
                    <Stepper.Step
                      key={`signer-${currentSigner.id}`}
                      label={`2. ${currentSigner.email}`}
                      description={description}
                      completed={currentSigner.signed}
                      color={currentSigner.status === "rejected" ? "red" : undefined}
                      completedIcon={
                        currentSigner.status === "rejected" ? (
                          <IconX size={20} />
                        ) : currentSigner.signed ? (
                          <IconCheck size={20} />
                        ) : undefined
                      }
                    />
                  );
                })()}
              </Stepper>
            ) : (
              <Stepper active={activeStep}>
                {(() => {
                  let rejectionOccurred = false;
                  return signers.map((signer, index) => {
                    const isRejected = signer.status === "rejected";
                    const isViewer = signer.role === "viewer";

                    const description = rejectionOccurred
                      ? `${signer.user_role}`
                      : `${signer.user_role} – ${
                          signer.role === "signer"
                            ? isRejected
                              ? "Rejeté"
                              : signer.signed
                              ? "Signé"
                              : "En attente"
                            : "Lecteur"
                        }`;

                    if (isRejected) {
                      rejectionOccurred = true;
                    }

                    return (
                      <Stepper.Step
                        key={signer.id}
                        label={`${index + 1}. ${signer.email}`}
                        description={description}
                        color={isRejected ? "red" : undefined}
                        completedIcon={isRejected ? <IconX size={20} /> : isViewer ? <IconEye size={20} /> : undefined}
                        completed={isViewer || signer.signed || isRejected}
                      />
                    );
                  });
                })()}
              </Stepper>
            )}
          </>
        )}

        {isUserTurn && !someoneRejected && (
          <Group justify="center" mt="md">
            <Button onClick={() => handleSign("approved")} color="green" loading={signing} disabled={signing}>
              Signer sans réserve
            </Button>

            {isFirstSigner ? (
              <Button onClick={handleDelete} color="red" loading={signing} disabled={signing}>
                Supprimer
              </Button>
            ) : (
              <>
                <Button onClick={() => setShowReserveModal(true)} color="yellow" loading={signing} disabled={signing}>
                  Signer avec réserve
                </Button>
                <Button onClick={() => handleSign("rejected")} color="red" loading={signing} disabled={signing}>
                  Refuser
                </Button>
              </>
            )}
          </Group>
        )}

        <Modal
          opened={showReserveModal}
          onClose={() => setShowReserveModal(false)}
          title="Ajouter un commentaire de réserve"
          centered
        >
          <Textarea
            label="Commentaire"
            placeholder="Expliquer la réserve ici..."
            value={reserveComment}
            onChange={(e) => setReserveComment(e.currentTarget.value)}
            autosize
            minRows={3}
          />
          <Group mt="md" justify="flex-end">
            <Button onClick={() => setShowReserveModal(false)} variant="default">
              Annuler
            </Button>
            <Button
              onClick={() => handleSign("reserved", reserveComment)}
              color="yellow"
              loading={signing}
              disabled={signing}
            >
              Confirmer la signature avec réserve
            </Button>
          </Group>
        </Modal>
      </Card>
    </Center>
  );
}
